import { confirm, Modal } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateFlow,
  useDuplicateFlow,
  useFlowQuery,
  useFlowsQuery,
  useUpdateFlow,
} from "../api/queries";
import { FlowForm } from "../ui/FlowForm";
import { FlowsFilter } from "../ui/FlowsFilter";
import { FlowsTable } from "../ui/FlowsTable";
import { FlowsToolbar } from "../ui/FlowsToolbar";

export function FlowsPage() {
  const nav = useNavigate({ from: "/app/flows" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/flows" });

  const list = useFlowsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["flows:create"]);
  const createFlow = useCreateFlow();
  const editModal = useDisclosure<{ id: string }>();
  const flowDetail = useFlowQuery(editModal.data?.id ?? "");
  const updateFlow = useUpdateFlow(editModal.data?.id ?? "");
  const createModal = useDisclosure();
  const duplicateFlow = useDuplicateFlow();

  return (
    <>
      <Header />

      <Main>
        <FlowsToolbar canManage={!!canManage} onCreate={createModal.open} />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <FlowsFilter />

          <FlowsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(f) => editModal.openWith({ id: f.id })}
            onView={(id) => nav({ to: "/app/flows/$id", params: { id } })}
            onCopy={async (id) => {
              try {
                const duplicated = await duplicateFlow.mutateAsync(id);
                if (duplicated?.id) {
                  nav({ to: "/app/flows/$id", params: { id: duplicated.id } });
                }
              } catch {
                // Error is already handled by the mutation
              }
            }}
          />
        </div>

        <Modal
          open={!!createModal.isOpen}
          onOpenChange={createModal.toggle}
          title="ສ້າງ Flow"
          size="md"
        >
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            <FlowForm
              onSubmit={async (vals) => {
                await createFlow.mutateAsync({
                  title: vals.title,
                  description: vals.description ?? undefined,
                  purpose: vals.purpose ?? undefined,
                  departmentId: vals.departmentId ?? undefined,
                  status: vals.status,
                });
                createModal.close();
                nav({
                  to: "/app/flows/$id",
                  params: { id: createFlow.data?.id ?? "" },
                });
              }}
              submitting={createFlow.isPending}
            />
          </div>
        </Modal>

        <Modal
          open={!!editModal.isOpen}
          onOpenChange={editModal.toggle}
          title="ແກ້ໄຂ Flow"
          size="md"
        >
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            <FlowForm
              initialValues={
                flowDetail.data
                  ? {
                      title: flowDetail.data.title,
                      description: flowDetail.data.description ?? undefined,
                      purpose: flowDetail.data.purpose ?? undefined,
                      departmentId: flowDetail.data.departmentId ?? undefined,
                      status: flowDetail.data.status,
                    }
                  : undefined
              }
              onSubmit={async (vals) => {
                // Check if department is being changed
                const isDepartmentChanging =
                  vals.departmentId !==
                  (flowDetail.data?.departmentId ?? undefined);

                if (isDepartmentChanging) {
                  const confirmed = await confirm({
                    title: "ປ່ຽນພະແນກຂອງ Flow",
                    description:
                      "ການປ່ຽນພະແນກຈະມີຜົນກະທົບດັ່ງນີ້:\n\n• ພະແນກຂອງທຸກຂັ້ນຕອນໃນ Flow ຈະຖືກອັບເດດເປັນພະແນກໃໝ່\n• ຕໍາແໜ່ງທີ່ບໍ່ຢູ່ໃນພະແນກໃໝ່ຈະຖືກລຶບອອກຈາກຂັ້ນຕອນ\n\nທ່ານແນ່ໃຈບໍ່ວ່າຈະດຳເນີນການຕໍ່?",
                    actionText: "ປ່ຽນພະແນກ",
                    cancelText: "ຍົກເລີກ",
                  });
                  if (!confirmed) return;
                }

                await updateFlow.mutateAsync({
                  title: vals.title,
                  description: vals.description ?? undefined,
                  purpose: vals.purpose ?? undefined,
                  departmentId: vals.departmentId ?? undefined,
                  status: vals.status,
                });
                editModal.close();
              }}
              submitting={updateFlow.isPending}
            />
          </div>
        </Modal>
      </Main>
    </>
  );
}
